import React, { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { authService, userService } from '../services';
import { User } from '../types';
import { TIMEOUTS } from '../constants/api';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  login: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  updateUser: (userData: User) => void;
  checkAuthStatus: () => void;
  refreshAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    userService.clearUserFromStorage();
  }, []);

  const checkAuthStatus = useCallback(() => {
    const authenticated = authService.isAuthenticated();
    
    // Дополнительная проверка срока действия токена
    if (authenticated && authService.isTokenExpired()) {
      // Токен истек, разлогиниваем пользователя
      logout();
      return;
    }
    
    setIsAuthenticated(authenticated);
    
    if (authenticated) {
      // Загружаем информацию о пользователе из localStorage
      const userData = userService.getUserFromStorage();
      if (userData) {
        setUser(userData);
      }
      
      // Здесь можно загрузить актуальную информацию о пользователе с сервера
      // fetchUserInfo();
    }
    
    setIsLoading(false);
  }, [logout]);

  useEffect(() => {
    checkAuthStatus();
    
    // Проверяем статус авторизации каждые 5 минут
    const interval = setInterval(checkAuthStatus, TIMEOUTS.SESSION_CHECK);
    
    return () => clearInterval(interval);
  }, [checkAuthStatus]);

  const login = useCallback((accessToken: string, refreshToken: string) => {
    authService.saveTokens(accessToken, refreshToken);
    setIsAuthenticated(true);
    
    // Здесь можно загрузить информацию о пользователе
    // fetchUserInfo(accessToken);
  }, []);

  const updateUser = useCallback((userData: User) => {
    setUser(userData);
    // Сохраняем в localStorage
    userService.saveUserToStorage(userData);
  }, []);

  const refreshAuth = useCallback(async () => {
    const { accessToken, refreshToken } = authService.getTokens();
    
    if (!accessToken || !refreshToken) {
      logout();
      return false;
    }

    try {
      const response = await authService.refreshTokens(accessToken, refreshToken);
      
      if (response.result === 'Authorized' && response.accessToken && response.refreshToken) {
        login(response.accessToken, response.refreshToken);
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.error('Failed to refresh tokens:', error);
      logout();
      return false;
    }
  }, [login, logout]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    updateUser,
    checkAuthStatus,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
